import type { CityConfig } from "../city-template/types";

export const sunnyvaleConfig: CityConfig = {
  city: "Sunnyvale",
  state: "California",
  stateAbbr: "CA",
  slug: "sunnyvale",
  route: "/sunnyvale",
  vitalRecordsIcon: "ri-file-text-line",
  lastModified: "2026-06-14",
  title: "Emergency Passport Sunnyvale, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Sunnyvale, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Silicon Valley and Santa Clara County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Sunnyvale, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Sunnyvale and Silicon Valley travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Sunnyvale%20California%20cityscape%20at%20golden%20hour%20with%20modern%20Silicon%20Valley%20office%20buildings%20and%20tech%20campuses%2C%20tree-lined%20suburban%20streets%20with%20green%20landscaping%2C%20clean%20modern%20planned%20community%20aesthetic%2C%20warm%20sunset%20light%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20innovative%20and%20serene%20suburban%20tech%20hub%20atmosphere&width=1800&height=700&seq=sunnyvale-hero-001&orientation=landscape",
  heroImageAlt: "Sunnyvale skyline at golden hour with modern Silicon Valley tech campuses and suburban streets",
  geoPosition: "37.3688;-122.0363",
  geoPlacename: "Sunnyvale, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in downtown San Francisco — approximately 45–55 minutes from Sunnyvale via US-101",
  },
  nearbyCitiesZips: [
    { name: "Sunnyvale", zips: "94085–94090" },
    { name: "Mountain View", zips: "94035, 94039–94043" },
    { name: "Santa Clara", zips: "95050–95056" },
    { name: "Cupertino", zips: "95014, 95015" },
    { name: "San Jose", zips: "95101–95103, 95110–95113, 95116, 95117, 95122–95126, 95128, 95131, 95132, 95134, 95136" },
    { name: "Milpitas", zips: "95035, 95036" },
    { name: "Los Altos", zips: "94022, 94024" },
    { name: "Palo Alto", zips: "94301–94306" },
    { name: "Menlo Park", zips: "94025–94029" },
    { name: "Redwood City", zips: "94061–94065" },
    { name: "Fremont", zips: "94536–94539, 94555" },
    { name: "Campbell", zips: "95008, 95009, 95011" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Silicon Valley departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in San Jose", route: "/san-jose" },
    { name: "Emergency passport help in San Francisco", route: "/san-francisco" },
    { name: "Expedited passport services in Santa Clara", route: "/santa-clara" },
    { name: "Urgent passport services in Oakland", route: "/oakland" },
    { name: "Same-day passport help in Berkeley", route: "/berkeley" },
    { name: "Passport courier services near Daly City", route: "/daly-city" },
    { name: "Rush passport help near Santa Rosa", route: "/santa-rosa" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Sunnyvale remotely via secure courier. We do not have a walk-in office in Sunnyvale — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Silicon Valley travelers.",
  fpcOfficeAddress: "400 S El Camino Real, San Mateo, CA 94402",
  fpcOfficeDistance: "Serving all of Santa Clara County",
  serviceAreaIntro: "From the downtown Sunnyvale historic district to the tech corridors near Moffett Field, from the Lakewood neighborhood to the Ponderosa area — Fast Passport Center serves the entire Sunnyvale community. We regularly help travelers from Mountain View, Palo Alto, Cupertino, and Los Altos get their passports fast.",
  nearbyCitiesIntro: "We serve all Sunnyvale neighborhoods and nearby Silicon Valley cities including Mountain View, Santa Clara, Cupertino, Palo Alto, Menlo Park, and San Jose. If you are in Santa Clara County or the Peninsula, we have you covered.",
  howItWorksIntro: "Most Sunnyvale travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With SFO and SJC both serving Silicon Valley, Sunnyvale travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Sunnyvale and Silicon Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Sunnyvale? Here are the most common ones we hear from Silicon Valley travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Silicon Valley international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Alex P.",
      location: "Sunnyvale, CA",
      rating: 5,
      text: "My startup's investor meeting in London was in 2 days and my passport was expired. Fast Passport Center got me a new one in 30 hours. Saved the trip.",
      service: "Emergency Passport",
    },
    {
      name: "Lisa W.",
      location: "Mountain View, CA",
      rating: 5,
      text: "Needed to renew for a family vacation to Singapore. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "James H.",
      location: "Palo Alto, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to New Zealand. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Rebecca M.",
      location: "Los Altos, CA",
      rating: 5,
      text: "As a frequent international business traveler from the Peninsula, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "sv-1",
      name: "Sunnyvale Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1295 W Olive Ave",
      city: "Sunnyvale",
      state: "CA",
      zip: "94086",
      phone: "(408) 739-0801",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Sunnyvale",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "sv-2",
      name: "Mountain View Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2111 N 1st St",
      city: "Mountain View",
      state: "CA",
      zip: "94043",
      phone: "(650) 965-5120",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from downtown Mountain View",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "sv-3",
      name: "Santa Clara Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1850 Clayton Rd",
      city: "Santa Clara",
      state: "CA",
      zip: "95050",
      phone: "(408) 296-2266",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.0 miles from Levi's Stadium",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for central Santa Clara County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "sv-4",
      name: "Cupertino Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "21701 Stevens Creek Blvd",
      city: "Cupertino",
      state: "CA",
      zip: "95014",
      phone: "(408) 996-0301",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from Vallco Shopping Mall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for western Santa Clara County residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "sv-5",
      name: "Palo Alto Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "380 Hamilton Ave",
      city: "Palo Alto",
      state: "CA",
      zip: "94301",
      phone: "(650) 322-3466",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Palo Alto",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Peninsula residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "sv-6",
      name: "Santa Clara County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "70 W Hedding St",
      city: "San Jose",
      state: "CA",
      zip: "95110",
      phone: "(408) 299-5688",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "10 miles from Sunnyvale",
      website: "https://www.sccgov.org/",
      websiteLabel: "Santa Clara County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Silicon Valley photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Santa Clara County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Silicon Valley applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Silicon Valley families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Silicon Valley travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Sunnyvale." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Sunnyvale address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Sunnyvale before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Sunnyvale for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Silicon Valley travelers." },
    { q: "How fast can I renew my passport in Sunnyvale?", a: "Fast Passport Center offers fast passport renewal in Sunnyvale in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my Asia trip?", a: "If your passport expires before your Asia trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Sunnyvale in 24–48 hours so you don't miss your flights." },
    { q: "Can I get a same-day passport appointment near Sunnyvale?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Sunnyvale residents." },
    { q: "Can I expedite a child passport in Sunnyvale?", a: "Yes. Fast Passport Center expedites child passports in Sunnyvale for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Silicon Valley families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Sunnyvale with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Sunnyvale?",
      a: "With Fast Passport Center's emergency courier service, Sunnyvale travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Sunnyvale?",
      a: "Popular options include the Sunnyvale Post Office (94086), Mountain View Post Office (94043), Santa Clara Post Office (95050), and Cupertino Post Office (95014). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Sunnyvale?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I work in Mountain View or Palo Alto?",
      a: "We serve all of Silicon Valley including Mountain View, Palo Alto, Los Altos, Menlo Park, and Redwood City. Our overnight shipping labels work from any UPS location, so your location within the tech corridor doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Sunnyvale?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Sunnyvale?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Sunnyvale, Mountain View, and Palo Alto offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Sunnyvale?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Sunnyvale?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Silicon Valley families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Sunnyvale", address: "130 E El Camino Real, Sunnyvale, CA 94087", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Mountain View", address: "1040 Grant Rd, Mountain View, CA 94040", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Sunnyvale", address: "1247 W El Camino Real, Sunnyvale, CA 94087", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Palo Alto", address: "3875 El Camino Real, Palo Alto, CA 94306", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Milpitas", address: "301 Ranch Dr, Milpitas, CA 95035", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Cupertino", address: "10630 S Foothill Blvd, Cupertino, CA 95014", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — San Jose", address: "1095 N 5th St, San Jose, CA 95112", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Sunnyvale", address: "150 Lawrence Station Rd, Sunnyvale, CA 94086", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Sunnyvale",
      address: "4201 Pearl Ave, San Jose, CA 95136",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — San Jose",
      address: "2222 Senter Rd, San Jose, CA 95112",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Los Gatos",
      address: "525 N Santa Cruz Ave, Los Gatos, CA 95030",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Redwood City",
      address: "300 Brewster Ave, Redwood City, CA 94063",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Peninsula and South Bay residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SJC",
      name: "San Jose International Airport",
      distance: "8 miles from Sunnyvale",
      address: "1701 Airport Blvd, San Jose, CA 95110",
      note: "The closest international airport for Sunnyvale travelers. Direct flights to Mexico, Canada, Europe, and Asia.",
      flightUrl: "https://www.flysanjose.com/",
      flightLabel: "San Jose Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "25 miles from Sunnyvale",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via US-101.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "30 miles from Sunnyvale",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Alternative international airport for East Bay and South Bay travelers. Accessible via I-880.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "Santa Clara County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for Santa Clara County",
      url: "https://www.sccassessor.org/",
    },
    {
      label: "Santa Clara County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for Santa Clara County",
      url: "https://www.scscourt.org/",
    },
  ],
};